import React, {useEffect, useMemo, useRef, useState} from "react";
import CodeMirror, {EditorView} from "@uiw/react-codemirror";
import {vscodeDark} from "@uiw/codemirror-theme-vscode";
import {python} from "@codemirror/lang-python";
import {javascript} from "@codemirror/lang-javascript";
import {html} from "@codemirror/lang-html";
import {css} from "@codemirror/lang-css";
import TextBlock from "../blocks/TextBlock";
import BlockRenderer from "../blocks/BlockRenderer";
import axios from "axios";
import {useParams} from "react-router-dom";
import {useAuth} from "../../../../context/AuthProvider";
import {USER_API_BASE} from "../../../../utils/apiConfig";


const LANGUAGE_EXTENSIONS = {
    python: () => python(),
    javascript: () => javascript(),
    js: () => javascript(),
    html: () => html(),
    css: () => css(),
};

const editorTheme = EditorView.theme({
    "&": {fontSize: "14px"},
    ".cm-content": {padding: "16px 0"},
});


/* ---------- Blocks list (delegates to BlockRenderer) ---------- */

const Blocks = ({blocks = [], onComplete}) => (
    <div className="flex flex-col gap-3">
        {blocks.map((block) => (
            <BlockRenderer
                key={block.id}
                block={block}
                onComplete={onComplete}
            />
        ))}
    </div>
);
/* ---------- Main component ---------- */

const ContentProject = ({content}) => {
    const {user} = useAuth();
    const {content_id} = useParams(); // adjust to your route param name

    // 1) Derived data first (no state needed)
    const flatSteps = useMemo(
        () =>
            (content?.parts || []).flatMap((part, partIndex) =>
                (part.steps || []).map((step, stepIndex) => ({
                    ...step,
                    partId: part.id,
                    partName: part.name,
                    partIndex,
                    stepIndex,
                }))
            ),
        [content]
    );

    // 2) Saved progress, read once on first render
    const storageKey = `codepro:project:${user?.id}:${content_id}`;

    const [saved] = useState(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });

    // 3) State, initialized from `saved`
    const [activeTab, setActiveTab] = useState("code");

    const [currentIndex, setCurrentIndex] = useState(() =>
        Math.min(saved?.currentIndex ?? 0, Math.max(flatSteps.length - 1, 0))
    );

    const [completedIds, setCompletedIds] = useState(() => {
        const validIds = new Set(flatSteps.map(s => s.id));
        return new Set((saved?.completedIds ?? []).filter(id => validIds.has(id)));
    });

    const [code, setCode] = useState(
        saved?.code ?? flatSteps[0]?.editors?.[0]?.starterCode ?? ""
    );
    const [stdin, setStdin] = useState(saved?.stdin ?? "");
    const [checkpoints, setCheckpoints] = useState(saved?.checkpoints ?? {});

    const [showHints, setShowHints] = useState(false);
    const [running, setRunning] = useState(false);
    const [output, setOutput] = useState("");
    const [failedStepId, setFailedStepId] = useState(null);

    // 4) Values derived from state
    const currentStep = flatSteps[currentIndex];
    const isCurrentCompleted = currentStep && completedIds.has(currentStep.id);

    const lastStepId = useRef(currentStep?.id);

    const runCode = async () => {
        if (!currentStep || running) return;

        setRunning(true);
        setActiveTab("result");
        setOutput("");
        setFailedStepId(null);

        const url = `${USER_API_BASE}/${user.id}/practice`;

        try {
            // 1) enqueue the job
            const {data: start} = await axios.post(
                url,
                {
                    content_id,
                    stepId: currentStep.id,
                    code,
                    stdin: stdin.replace(/\r\n/g, "\n"),
                    checkpoints,
                },
                {withCredentials: true}
            );

            if (!start.success) {
                setOutput(start.message || "خطا در اجرای کد");
                return;
            }

            // 2) poll for the result (max ~30s)
            for (let attempt = 0; attempt < 60; attempt++) {
                await sleep(500);

                const {data: result} = await axios.get(
                    `${url}/${start.jobId}`,
                    {withCredentials: true}
                );

                if (result.status === "completed") {
                    const {stdout = "", stderr = ""} = result.execution || {};
                    const text = [stdout, stderr].filter(Boolean).join("\n");
                    setOutput(text || "برنامه بدون خروجی اجرا شد.");

                    if (result.validated) {
                        setFailedStepId(null);
                        markStepDone();
                    } else {
                        setFailedStepId(result.firstInvalidStep || currentStep.id);
                    }
                    return;
                }

                if (result.status === "failed") {
                    setOutput(result.message || "اجرای برنامه با خطا مواجه شد");
                    return;
                }
                // waiting / active / delayed -> keep polling
            }

            setOutput("زمان اجرای برنامه به پایان رسید.");
        } catch (err) {
            console.error("RUN ERROR:", err);
            setOutput(err.response?.data?.message || "خطا در ارتباط با سرور");
        } finally {
            setRunning(false);
        }
    };

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    useEffect(() => {
        if (lastStepId.current === currentStep?.id) return; // mount: keep restored code
        lastStepId.current = currentStep?.id;

        const starter = currentStep?.editors?.find(e => e.slot === "main")?.starterCode
            ?? currentStep?.editors?.[0]?.starterCode;
        if (starter) setCode(starter);
        setShowHints(false);
        setFailedStepId(null);
    }, [currentStep?.id]);

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                localStorage.setItem(
                    storageKey,
                    JSON.stringify({
                        code,
                        stdin,
                        currentIndex,
                        completedIds: [...completedIds],
                        checkpoints,
                    })
                );
            } catch (err) {
                console.warn("Could not save progress:", err);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [storageKey, code, stdin, currentIndex, completedIds, checkpoints]);

    const language = (content?.workspace?.language || "python").toLowerCase();

    const extensions = useMemo(() => {
        const langExt = LANGUAGE_EXTENSIONS[language]?.();
        return [editorTheme, ...(langExt ? [langExt] : [])];
    }, [language]);

    const markStepDone = () => {
        if (!currentStep) return;
        setCompletedIds(prev => new Set(prev).add(currentStep.id));
    };

    const goNext = () => {
        if (currentIndex < flatSteps.length - 1) setCurrentIndex(currentIndex + 1);
    };

    // A step is reachable if it's completed, current, or directly follows a completed one
    const isReachable = (index) =>
        index <= currentIndex || completedIds.has(flatSteps[index - 1]?.id);

    // Group flat steps back by part for the sidebar
    const parts = content?.parts || [];

    return (
        <div className="w-full h-full px-4 py-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* ===================== LEFT: INSTRUCTIONS ===================== */}
                    <div className="min-w-0">
                        <div className="rounded-2xl border h-full block border-gray-200 bg-white p-6">

                            {/* Current step */}
                            {currentStep ? (
                                <div className="mb-8">
                                    <div className="text-xs text-gray-400 mb-1">
                                        {currentStep.partName} · مرحله {currentStep.stepIndex + 1}
                                    </div>

                                    <h2 className="text-lg font-bold mb-4">دستورالعمل</h2>

                                    <Blocks blocks={currentStep.instruction?.blocks} onComplete={markStepDone}/>

                                    {/* Hints */}
                                    {currentStep.hints?.length > 0 && (
                                        <div className="mt-4">
                                            <button
                                                type="button"
                                                onClick={() => setShowHints(v => !v)}
                                                className="text-sm text-blue-600 hover:underline"
                                            >
                                                {showHints ? "پنهان کردن راهنمایی" : "نمایش راهنمایی"}
                                            </button>
                                            {showHints && (
                                                <div className="mt-2 rounded-xl bg-blue-50 p-4">
                                                    {currentStep.hints.map((hint, i) => (
                                                        <Blocks key={hint.id || i} blocks={hint.blocks}/>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {(() => {
                                        const failedStep = flatSteps.find(s => s.id === failedStepId);
                                        if (!failedStep) return null;

                                        return (
                                            <div className="mt-6 rounded-xl bg-red-50 border border-red-200 p-4">
                                                <Blocks blocks={failedStep.feedback?.incorrect?.blocks} />
                                            </div>
                                        );
                                    })()}

                                    {/* After completion: success feedback + explanation */}
                                    {isCurrentCompleted && (
                                        <div className="mt-6 flex flex-col gap-4">
                                            <div className="rounded-xl bg-green-50 border border-green-200 p-4">
                                                <Blocks blocks={currentStep.feedback?.correct?.blocks}/>
                                            </div>

                                            {currentStep.explain?.blocks?.length > 0 && (
                                                <div>
                                                    <h3 className="text-sm font-semibold mb-2">توضیح</h3>
                                                    <Blocks blocks={currentStep.explain.blocks}/>
                                                </div>
                                            )}

                                            {currentIndex < flatSteps.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={goNext}
                                                    className="self-start rounded-xl bg-black text-white px-5 py-2 text-sm hover:bg-gray-800 transition"
                                                >
                                                    مرحله بعد
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-gray-400 text-sm">
                                    مراحل پروژه هنوز تعریف نشده‌اند.
                                </p>
                            )}

                            {/* Progress timeline grouped by part */}
                            <div className="border-t border-gray-100 pt-6">
                                <h2 className="text-base font-bold mb-4">مراحل پروژه</h2>

                                {parts.map((part) => (
                                    <div key={part.id} className="mb-6 last:mb-0">
                                        <h3 className="text-sm font-semibold text-gray-500 mb-3">
                                            {part.name}
                                        </h3>

                                        <div className="flex flex-col">
                                            {(part.steps || []).map((step, i) => {
                                                const index = flatSteps.findIndex(s => s.id === step.id);
                                                const completed = completedIds.has(step.id);
                                                const active = index === currentIndex;
                                                const disabled = !isReachable(index);
                                                const isLast = i === part.steps.length - 1;

                                                return (
                                                    <button
                                                        key={step.id}
                                                        type="button"
                                                        disabled={disabled}
                                                        onClick={() => setCurrentIndex(index)}
                                                        className="relative flex gap-3 pb-5 last:pb-0 text-right disabled:cursor-not-allowed"
                                                    >
                                                        {!isLast && (
                                                            <div
                                                                className="absolute right-[13px] top-7 bottom-0 w-px bg-gray-200"/>
                                                        )}

                                                        <div
                                                            className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs
                                                                ${completed
                                                                ? "border-green-500 bg-green-500 text-white"
                                                                : active
                                                                    ? "border-black bg-black text-white"
                                                                    : disabled
                                                                        ? "border-gray-200 bg-gray-100 text-gray-300"
                                                                        : "border-gray-300 bg-white text-gray-500"}`}
                                                        >
                                                            {completed ? "✓" : i + 1}
                                                        </div>

                                                        <span
                                                            className={`pt-1 text-sm ${
                                                                disabled ? "text-gray-300" : active ? "font-semibold text-gray-900" : "text-gray-600"
                                                            }`}
                                                        >
                                                            مرحله {i + 1}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ===================== RIGHT: EDITOR ===================== */}
                    <div className="min-w-0">
                        <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                            <div className="flex border-b border-gray-200">
                                {[["code", "کد"], ["result", "نتیجه"]].map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setActiveTab(key)}
                                        className={`px-6 py-3 text-sm font-medium transition ${
                                            activeTab === key
                                                ? "border-b-2 border-black text-black"
                                                : "text-gray-500 hover:text-gray-800"}`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {activeTab === "code" && (
                                <div dir="ltr">
                                    <div className="h-[480px]">
                                        <CodeMirror
                                            value={code}
                                            height="480px"
                                            theme={vscodeDark}
                                            extensions={extensions}
                                            onChange={(value) => setCode(value)}
                                            basicSetup={{
                                                lineNumbers: true,
                                                foldGutter: true,
                                                highlightActiveLine: true,
                                                autocompletion: true,
                                            }}
                                        />
                                    </div>

                                    {/* ===================== INPUT ===================== */}
                                    <div className="border-t border-gray-200 p-4" dir="rtl">
                                        <label
                                            htmlFor="program-input"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            ورودی برنامه
                                            <span className="text-xs text-gray-400 mr-2">
                    (هر خط یک ورودی)
                </span>
                                        </label>

                                        <textarea
                                            id="program-input"
                                            dir="ltr"
                                            rows={4}
                                            value={stdin}
                                            onChange={(e) => setStdin(e.target.value)}
                                            placeholder={"مثال:\n10\n20"}
                                            spellCheck={false}
                                            className="w-full rounded-xl border border-gray-200 bg-gray-50 p-3 font-mono text-sm
                           focus:outline-none focus:ring-2 focus:ring-black/10 resize-y"
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === "result" && (
                                <div className="h-[600px] bg-gray-950 text-white p-5 font-mono overflow-auto" dir="ltr">
                                    <div className="text-gray-400 text-sm mb-3 w-full p-2">نتیجه‌ی اجرا</div>
                                    <pre className="whitespace-pre-wrap">
    {running ? "در حال اجرا..." : output || "خروجی اجرای برنامه اینجا نمایش داده می‌شود."}
</pre>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end mt-4">
                            <div className="flex justify-end mt-4">
                                <button
                                    type="button"
                                    disabled={running}
                                    className="rounded-xl bg-black text-white px-6 py-3 font-medium hover:bg-gray-800 transition disabled:opacity-50"
                                    onClick={runCode}
                                >
                                    {running ? "در حال اجرا..." : "اجرای کد"}
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ContentProject;